'use client';

import { Bell, UserRound } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState, useSyncExternalStore } from 'react';
import CommentLogin from './comment-login';
import MessageCenter from './message-center';
import {
  readWikiUser,
  WIKI_AUTH_EVENT,
  WIKI_USER_KEY,
  WIKI_TOKEN_KEY,
  type WikiUser,
} from '../lib/wiki-auth';
import { wikiProfilePath } from '../lib/wiki-profile-path';
import './site-account-controls.css';
import { refreshWikiSession } from '../lib/gamekee-api';
import { LOCAL_AUTH_BYPASS, LOCAL_WIKI_USER } from '../lib/local-auth';

const subscribe = (callback: () => void) => {
  window.addEventListener('storage', callback);
  window.addEventListener(WIKI_AUTH_EVENT, callback);
  return () => {
    window.removeEventListener('storage', callback);
    window.removeEventListener(WIKI_AUTH_EVENT, callback);
  };
};
const snapshot = () => {
  if (LOCAL_AUTH_BYPASS) return JSON.stringify(LOCAL_WIKI_USER);
  try {
    return localStorage.getItem(WIKI_TOKEN_KEY)
      ? localStorage.getItem(WIKI_USER_KEY) || ''
      : '';
  } catch {
    return '';
  }
};
const serverSnapshot = () => '';
export default function SiteAccountControls({
  gameDomain,
}: {
  gameDomain?: string;
}) {
  const saved = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  let user: WikiUser | null = null;
  try {
    const parsed = saved ? JSON.parse(saved) : null;
    user = parsed?.name ? parsed : null;
  } catch {
    /* Invalid storage behaves as signed out. */
  }
  const [loginOpen, setLoginOpen] = useState(false);
  const [failedAvatar, setFailedAvatar] = useState('');
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [destination, setDestination] = useState<
    'messages' | 'profile' | 'stay'
  >('profile');
  useEffect(() => {
    refreshWikiSession().catch(() => {});
    const requestLogin = () => {
      setDestination('stay');
      setLoginOpen(true);
    };
    window.addEventListener('game-wiki-login-request', requestLogin);
    return () =>
      window.removeEventListener('game-wiki-login-request', requestLogin);
  }, []);
  const goProfile = () => {
    window.location.assign(wikiProfilePath(gameDomain));
  };
  const finish = () => {
    setLoginOpen(false);
    if (destination === 'messages') setMessagesOpen(true);
    else if (destination === 'profile') goProfile();
  };
  const open = (next: 'messages' | 'profile') => {
    if (readWikiUser()) {
      if (next === 'messages') setMessagesOpen(true);
      else goProfile();
    } else {
      setDestination(next);
      setLoginOpen(true);
    }
  };
  return (
    <div className="site-account-controls" aria-label="用户功能">
      <button type="button" onClick={() => open('messages')}>
        <Bell />
        <span>消息</span>
      </button>
      <button type="button" onClick={() => open('profile')}>
        {user?.avatar && user.avatar !== failedAvatar ? (
          <Image
            src={user.avatar}
            alt=""
            width={18}
            height={18}
            unoptimized
            onError={() => setFailedAvatar(user.avatar || '')}
          />
        ) : (
          <UserRound />
        )}
        <span>{user ? '个人中心' : '登录'}</span>
      </button>
      <CommentLogin
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onLogin={finish}
      />
      <MessageCenter
        open={messagesOpen}
        onOpenChange={setMessagesOpen}
        user={user}
        gameDomain={gameDomain}
      />
    </div>
  );
}
